import { Text } from 'tamagui';

/**
 * PSLinkButton — a text-only link-style button (atom).
 *
 * The secondary CTAs in the auth flow — "Resend code", "Use a different
 * number", "Login with email instead". Brand-purple, underlined text with a
 * press + disabled state, and nothing else: no surface, no border, no padding
 * frame. That's the whole point of it existing alongside PSButton — same button
 * role (a tap target with a label), opposite weight (bare text vs a filled
 * surface).
 *
 * It's a `Text` with `onPress`, not a `Button` wrapping a `Text`: a link IS its
 * text, so the tap target should be exactly the glyphs (a Button frame would add
 * hit area and a min-height around it). RN text doesn't cascade, so the color +
 * underline live on this one `Text` directly.
 *
 * Disabled dims to 0.4 (matching PSButton) rather than going gray — it stays
 * recognizably the same brand link, just inert. `title` is a string like
 * PSButton's; the atom owns the brand/underline/size so every link reads the
 * same (no `children`, so the styling can't be bypassed).
 */
type PSLinkButtonProps = {
  variant?: 'brand' | 'neutral';
  title: string;
  onPress?: () => void;
  disabled?: boolean;
};

export default function PSLinkButton({
  title,
  onPress,
  disabled = false,
  variant = 'brand',
}: PSLinkButtonProps) {
  return (
    <Text
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      cursor={disabled || !onPress ? undefined : 'pointer'}
      opacity={disabled ? 0.4 : 1}
      pressStyle={onPress && !disabled ? { opacity: 0.6 } : undefined}
      fontFamily="$body"
      fontSize="$base"
      fontWeight="$semibold"
      color={variant === 'brand' ? '$brand' : '$color.textDark'}
      textDecorationLine="underline"
    >
      {title}
    </Text>
  );
}
